import React from 'react';
import {
  Box,
  Heading,
  Image,
  Link as ChakraLink,
  Text,
} from '@chakra-ui/react';
import { toPlainText } from '../../utils/toPlainText';

/** Chiave React senza indice array (fingerprint del JSON, senza operatori bitwise). */
export const stableJsonKey = (prefix, value) => {
  try {
    const s = JSON.stringify(value);
    const h = s
      .split('')
      .reduce((acc, ch) => (31 * acc + ch.charCodeAt(0)) % 2147483647, 0);
    return `${prefix}-${h}-${s.length}`;
  } catch {
    return `${prefix}-0`;
  }
};

export const renderInlineChildren = (children = []) =>
  children.map((child) => {
    const text = toPlainText(child?.text);
    const k = stableJsonKey('inline', child);
    if (child.bold) return <strong key={`b-${k}`}>{text}</strong>;
    if (child.italic) return <em key={`i-${k}`}>{text}</em>;
    if (child.underline) return <u key={`u-${k}`}>{text}</u>;
    if (child.type === 'link' && child.url) {
      const href = toPlainText(child.url) || '#';
      return (
        <ChakraLink key={`l-${k}`} href={href} color="#36a9e1" isExternal>
          {renderInlineChildren(child.children || [{ text: href }])}
        </ChakraLink>
      );
    }
    return <React.Fragment key={`t-${k}`}>{text}</React.Fragment>;
  });

/**
 * @param {unknown[]} content
 * @param {{ prose?: boolean }} opts
 */
export const renderBlocksRichText = (content = [], opts = {}) => {
  const { prose } = opts;

  return (Array.isArray(content) ? content : []).map((block) => {
    const bk = stableJsonKey('body', block);
    if (block.type === 'heading') {
      const sizeByLevel = {
        1: 'xl',
        2: 'lg',
        3: 'md',
        4: 'sm',
        5: 'sm',
        6: 'sm',
      };
      return (
        <Heading
          key={`h-${bk}`}
          as={`h${Math.min(6, Math.max(2, block.level || 3))}`}
          size={sizeByLevel[block.level] || 'md'}
          mt={6}
          mb={3}
          color="gray.800"
        >
          {renderInlineChildren(block.children || [])}
        </Heading>
      );
    }
    if (block.type === 'paragraph') {
      return prose ? (
        <Text
          key={`p-${bk}`}
          lineHeight={1.85}
          mb={5}
          color="gray.700"
          fontSize={{ base: 'md', md: 'lg' }}
        >
          {renderInlineChildren(block.children || [])}
        </Text>
      ) : (
        <Text key={`p-${bk}`} lineHeight={1.8} mb={4} color="gray.700">
          {renderInlineChildren(block.children || [])}
        </Text>
      );
    }
    if (block.type === 'list') {
      return (
        <Box
          key={`list-${bk}`}
          as={block.format === 'ordered' ? 'ol' : 'ul'}
          pl={6}
          mb={4}
        >
          {(block.children || []).map((item) => (
            <Box
              as="li"
              key={`li-${stableJsonKey('li', item)}`}
              color="gray.700"
              mb={2}
            >
              {renderInlineChildren(item.children || [])}
            </Box>
          ))}
        </Box>
      );
    }
    if (block.type === 'quote') {
      return (
        <Box
          key={`q-${bk}`}
          borderLeft="4px solid"
          borderColor="#36a9e1"
          pl={4}
          py={2}
          mb={4}
          bg="gray.50"
        >
          <Text fontStyle="italic">
            {renderInlineChildren(block.children || [])}
          </Text>
        </Box>
      );
    }
    if (block.type === 'image' && block.image?.url) {
      return (
        <Box key={`img-${bk}`} my={5}>
          <Image
            src={block.image.url}
            alt={toPlainText(block.image.alternativeText) || 'Immagine'}
            borderRadius="md"
            w="100%"
          />
          {toPlainText(block.image.caption) ? (
            <Text fontSize="sm" color="gray.500" mt={2}>
              {toPlainText(block.image.caption)}
            </Text>
          ) : null}
        </Box>
      );
    }
    return null;
  });
};
